<form [formGroup]="form">
  <label>{{ 'dashboard.edit.title' | translate }}</label>
  <input type="text" formControlName="title" class="form-control" />

  <label>{{ 'dashboard.edit.description' | translate }}</label>
  <input type="text" formControlName="description" class="form-control" />

  @if (!isTemplateDashboard) {
    <label>{{
      'dashboard.edit.DailyScanTimesDashboardComponent.dataConnections'
        | translate
    }}</label>
    <p-multiselect
      [options]="dataConnections"
      formControlName="dataConnectionIds"
      optionValue="id"
      [filter]="true"
      [showClear]="true"
      [showToggleAll]="false"
      optionLabel="name"
      class="w-100"
    />
  }

  <label>{{
    'dashboard.edit.DailyScanTimesDashboardComponent.statuses' | translate
  }}</label>
  <p-multiselect
    [options]="statusOptions"
    formControlName="statuses"
    optionValue="value"
    [filter]="false"
    [showClear]="true"
    [showToggleAll]="false"
    optionLabel="key"
    class="w-100"
  >
    <ng-template let-selectedOption pTemplate="selectedItem">
      {{ 'job.statusOptions.' + selectedOption.key | translate }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{ 'job.statusOptions.' + option.key | translate }}
    </ng-template>
  </p-multiselect>

  <label>{{
    'dashboard.edit.DailyScanTimesDashboardComponent.range' | translate
  }}</label>
  <p-select
    formControlName="range"
    [options]="rangeOptions"
    optionLabel="key"
    optionValue="value"
    class="w-100"
  >
    <ng-template let-selectedOption pTemplate="selectedItem">
      {{
        'dashboard.edit.JobDashboardComponent.rangeOptions.' +
          selectedOption.key | translate
      }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{
        'dashboard.edit.JobDashboardComponent.rangeOptions.' + option.key
          | translate
      }}
    </ng-template>
  </p-select>

  @if (form.get('range')!.value === customRange) {
    <label>{{
      'dashboard.edit.JobDashboardComponent.rangeOptions.CUSTOM' | translate
    }}</label>
    <p-datepicker
      required
      [(ngModel)]="dateRange"
      [ngModelOptions]="{ standalone: true }"
      (ngModelChange)="customRangeChange($event)"
      selectionMode="range"
      dateFormat="dd.mm.yy"
      [showIcon]="true"
      [showButtonBar]="true"
      [numberOfMonths]="1"
      styleClass="w-100"
      inputStyleClass="w-100"
      [class.is-invalid]="dateRangeError"
    />
    @if (dateRangeError) {
      <div class="invalid-feedback d-block">{{ dateRangeError }}</div>
    }
    <small class="text-muted d-block mb-2">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.maxDateRange'
          | translate: { days: maxDateRangeDays }
      }}
    </small>
  }

  <label for="daily-scan-times-sort-order">{{
    'dashboard.edit.DailyScanTimesDashboardComponent.sortOrder' | translate
  }}</label>
  <p-select
    inputId="daily-scan-times-sort-order"
    formControlName="sortOrder"
    [options]="sortOrderOptions"
    class="w-100"
  >
    <ng-template let-option pTemplate="selectedItem">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.sortOrderOptions.' +
          option | translate
      }}
    </ng-template>
    <ng-template let-option pTemplate="item">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.sortOrderOptions.' +
          option | translate
      }}
    </ng-template>
  </p-select>

  <hr class="my-3" />

  <div class="d-flex justify-content-between align-items-center mb-2">
    <label class="mb-0">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.scanTimestampField'
          | translate
      }}
    </label>
    @if (!isTemplateDashboard) {
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary"
        (click)="discoverFields()"
        [disabled]="isLoadingFields"
      >
        @if (isLoadingFields) {
          <span class="spinner-border spinner-border-sm me-1"></span>
        }
        {{
          'dashboard.edit.DailyScanTimesDashboardComponent.discoverFields'
            | translate
        }}
      </button>
    }
  </div>

  @if (availableFields.length === 0 && !isLoadingFields) {
    <div class="alert alert-info py-2">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.noFieldsFound'
          | translate
      }}
    </div>
  }

  <p-select
    formControlName="scanTimestampFieldKey"
    [options]="getTimestampFieldOptions()"
    optionLabel="label"
    optionValue="value"
    [filter]="true"
    [showClear]="true"
    placeholder="Select field"
    class="w-100"
  />
  <small class="text-muted d-block mb-2">
    {{
      'dashboard.edit.DailyScanTimesDashboardComponent.scanTimestampFieldHint'
        | translate
    }}
  </small>

  <hr class="my-3" />

  <div class="d-flex justify-content-between align-items-center mb-2">
    <label class="mb-0">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.filters' | translate
      }}
      <span class="text-muted small ms-1"
        >({{ filters.length }}/{{ maxFilters }})</span
      >
    </label>
    <button
      type="button"
      class="btn btn-sm btn-outline-primary"
      (click)="addFilter()"
      [disabled]="filters.length >= maxFilters"
    >
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.addFilter' | translate
      }}
    </button>
  </div>
  @if (filters.length >= maxFilters) {
    <div class="alert alert-info py-1 mb-2">
      {{
        'dashboard.edit.DailyScanTimesDashboardComponent.maxFiltersReached'
          | translate: { max: maxFilters }
      }}
    </div>
  }

  <div formArrayName="filters">
    @for (filter of filters.controls; track filter; let i = $index) {
      <div class="card mb-2">
        <div class="card-body py-2">
          <div [formGroupName]="i" class="row align-items-end">
            <div class="col-4">
              <label class="small">{{
                'dashboard.edit.DailyScanTimesDashboardComponent.fieldKey'
                  | translate
              }}</label>
              <p-select
                formControlName="fieldKey"
                [options]="getFieldOptions()"
                optionLabel="label"
                optionValue="value"
                [filter]="true"
                [showClear]="true"
                placeholder="Select field"
                class="w-100"
              />
            </div>
            <div class="col-3">
              <label class="small">{{
                'dashboard.edit.DailyScanTimesDashboardComponent.operator'
                  | translate
              }}</label>
              <p-select
                formControlName="operator"
                [options]="filterOperators"
                optionLabel="key"
                optionValue="value"
                class="w-100"
              >
                <ng-template let-selectedOption pTemplate="selectedItem">
                  {{
                    'dashboard.edit.JobResultAnalyticsDashboardComponent.filterOperators.' +
                      selectedOption.key | translate
                  }}
                </ng-template>
                <ng-template let-option pTemplate="item">
                  {{
                    'dashboard.edit.JobResultAnalyticsDashboardComponent.filterOperators.' +
                      option.key | translate
                  }}
                </ng-template>
              </p-select>
            </div>
            <div class="col-3">
              <label class="small">{{
                'dashboard.edit.DailyScanTimesDashboardComponent.value'
                  | translate
              }}</label>
              <input
                type="text"
                formControlName="value"
                class="form-control form-control-sm"
              />
            </div>
            <div class="col-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger w-100"
                (click)="removeFilter(i)"
              >
                <span class="material-symbols-outlined">delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    }
  </div>
</form>
